import React, { useState } from 'react';
import styled from 'styled-components';
import { getTranslatedString } from 'helpers/i18n';
import { featureEnabled } from 'helpers/ToggleBot';

import DrawerTabs from 'components/ui/DrawerTabs';
import PageHeader from 'components/ui/PageHeader';

import ProjectOverview from './ProjectOverview';
import ProjectTickets from './ProjectTickets';
import ProjectPurchaseOrders from './ProjectPurchaseOrders';
import MobileNavDropdown from 'components/ui/MobileNavDropdown/MobileNavDropdown';
import { StyledMain, StyledHeaderContainer } from 'helpers/StyledTableComponentsHelper';
import { media } from 'helpers/MediaHelper';
import CtaBreadCrumb from 'components/ui/CtaBreadCrumb';

import { isIOS, isIOS13, isIPad13, isMobileSafari } from 'react-device-detect';

const StyledCard = styled.div`
  background-color: white;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  padding: 24px 0;
`;

const StyledDetailsCard = styled(StyledCard)`
  margin: 0;
  padding-top: 8px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
`;

const RestyledMain = styled(StyledMain)`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  overflow-y: scroll;
  height: calc(100vh - 53px - ${isIOS13 || isIOS || isIPad13 || isMobileSafari ? '100px' : '0px'});
`;

const HeaderContainer = styled(StyledHeaderContainer)`
  flex-direction: row !important;
  padding: 2rem;
  width: 100%;
  max-width: 1800px;
  margin-left: auto;
  margin-right: auto;

  ${media.mobile`
    position: inherit !important;
    padding: 2rem 0;
    margin: inherit;
  `};
`;

const StyledHeader = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  max-width: 100%;
  position: inherit !important;
`;

const StyledBackWrapper = styled.div`
  padding: 0px 0 24px;
  ${media.mobile`
    padding: 54px 16px 32px;
  `};
`;

const StyledDrawerTabs = styled(DrawerTabs)`
  overflow: scroll !important;
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const StyledTabPane = styled(DrawerTabs.TabPane)`
  background-color: white;
`;

const OVERVIEW_TAB = '1';
const TICKETS_TAB = '2';
const PURCHASE_ORDER_TAB = '3';

const ProjectDetails = ({
  fetchProject,
  selectedProject,
  properties,
  units,
  fetchUnitsOfProperty,
  putProject,
  handleBack,
  getAllPurchaseOrders,
  fetchFilteredProjectPurchaseOrders,
  foundPurchaseOrders,
  linkedPurchaseOrders,
  linkPurchaseOrderToProject,
  unlinkPurchaseOrderFromProject,
  fetchFilteredProjectTickets,
  tickets,
  currentLanguage,
  linkTicketToProject,
  unlinkTicketFromProject,
}) => {
  const [tabIndex, setTabIndex] = useState(OVERVIEW_TAB);

  const changeTab = key => {
    setTabIndex(key);
  };

  const {
    id,
    name,
    description,
    created_at,
    due_at,
    maintenance_tickets,
    purchase_orders,
    property_id,
    status,
  } = selectedProject;

  const isMobile = window.matchMedia('(max-width: 750px)').matches;

  const ProjectOverviewTab = (
    <ProjectOverview
      numTicketsLinked={maintenance_tickets}
      numPurchaseOrders={purchase_orders}
      dueDate={due_at}
      dateCreated={created_at}
      selectedProject={selectedProject}
      properties={properties}
      units={units}
      fetchUnitsOfProperty={fetchUnitsOfProperty}
      fetchProject={fetchProject}
      putProject={putProject}
    />
  );

  const ProjectTicketsTab = (
    <ProjectTickets
      projectID={id}
      propertyID={property_id}
      fetchFilteredProjectTickets={fetchFilteredProjectTickets}
      fetchProject={fetchProject}
      tickets={tickets}
      linkTicketToProject={linkTicketToProject}
      unlinkTicketFromProject={unlinkTicketFromProject}
      currentLanguage={currentLanguage}
    />
  );

  const ProjectPurchaseOrdersTab = (
    <ProjectPurchaseOrders
      fetchProject={fetchProject}
      selectedProject={selectedProject}
      fetchFilteredProjectPurchaseOrders={fetchFilteredProjectPurchaseOrders}
      getAllPurchaseOrders={getAllPurchaseOrders}
      foundPurchaseOrders={foundPurchaseOrders}
      linkedPurchaseOrders={linkedPurchaseOrders}
      linkPurchaseOrderToProject={linkPurchaseOrderToProject}
      unlinkPurchaseOrderFromProject={unlinkPurchaseOrderFromProject}
    />
  );

  return (
    <RestyledMain>
      <HeaderContainer>
        <StyledHeader>
          <StyledBackWrapper>
            <CtaBreadCrumb
              onClick={handleBack}
              breadcrumbBackTitle={getTranslatedString(
                'landlordPortal.maintenance.projects.maintenanceProjects'
              )}
              breadCrumbCurrentTitle={name}
            />
          </StyledBackWrapper>
          <PageHeader name={name} status={status} description={description} />
          {isMobile && <MobileNavContainer tabIndex={tabIndex} onChange={changeTab} />}
          <StyledDetailsCard>
            {isMobile ? (
              <TabForms
                tabIndex={tabIndex}
                ProjectOverviewTab={ProjectOverviewTab}
                ProjectTicketsTab={ProjectTicketsTab}
                ProjectPurchaseOrdersTab={ProjectPurchaseOrdersTab}
              />
            ) : (
              <StyledDrawerTabs
                defaultActiveKey={tabIndex}
                onChange={changeTab}
                color="inherit"
                padding="0 32px"
              >
                <StyledTabPane
                  tab={getTranslatedString('landlordPortal.maintenance.projects.overviewTabHeader')}
                  key={OVERVIEW_TAB}
                >
                  {ProjectOverviewTab}
                </StyledTabPane>
                <StyledTabPane
                  tab={`${getTranslatedString(
                    'landlordPortal.maintenance.projects.ticketsTabHeader'
                  )} (${maintenance_tickets})`}
                  key={TICKETS_TAB}
                  padding="32px 0 0 0"
                >
                  {ProjectTicketsTab}
                </StyledTabPane>
                {featureEnabled('purchase_order') && (
                  <StyledTabPane
                    tab={`${getTranslatedString(
                      'landlordPortal.maintenance.projects.purchaseOrdersTabHeader'
                    )} (${purchase_orders})`}
                    key={PURCHASE_ORDER_TAB}
                  >
                    {ProjectPurchaseOrdersTab}
                  </StyledTabPane>
                )}
              </StyledDrawerTabs>
            )}
          </StyledDetailsCard>
        </StyledHeader>
      </HeaderContainer>
    </RestyledMain>
  );
};

const MobileNavContainer = ({ tabIndex, onChange }) => {
  const tabs = [
    {
      value: OVERVIEW_TAB,
      label: getTranslatedString('landlordPortal.maintenance.projects.overviewTabHeader'),
    },
    {
      value: TICKETS_TAB,
      label: getTranslatedString('landlordPortal.maintenance.projects.ticketsTabHeader'),
    },
    {
      value: PURCHASE_ORDER_TAB,
      label: getTranslatedString('landlordPortal.maintenance.projects.purchaseOrdersTabHeader'),
      hidden: !featureEnabled('purchase_order'),
    },
  ];

  const filteredTabs = tabs.filter(tab => !tab.hidden);
  const currentTab = filteredTabs.find(tab => tab.value === tabIndex);

  return <MobileNavDropdown tabs={filteredTabs} currentValue={currentTab} onChange={onChange} />;
};

const TabForms = ({
  tabIndex,
  ProjectOverviewTab,
  ProjectTicketsTab,
  ProjectPurchaseOrdersTab,
}) => {
  switch (tabIndex) {
    case OVERVIEW_TAB:
      return ProjectOverviewTab;
    case TICKETS_TAB:
      return ProjectTicketsTab;
    case PURCHASE_ORDER_TAB:
      return ProjectPurchaseOrdersTab;
    default:
      return ProjectOverviewTab;
  }
};

export default ProjectDetails;
